import { getFunctions, httpsCallable } from "firebase/functions";
import { isTerminalCallStatus } from "./callLifecycleController";

const HEARTBEAT_INTERVAL_MS = 10000;

const ACTIVATION_BASE_BACKOFF_MS = 2000;
const ACTIVATION_MAX_BACKOFF_MS = 10000;

// N3C1 section B: avoids a zero/negative-delay tight loop when the
// latency-compensated estimate overshoots, and gives an in-flight
// heartbeat a moment to finish before retrying the boundary fire.
const BOUNDARY_MIN_RETRY_DELAY_MS = 200;

const heartbeatByCallId = new Map();

// N3C section E: retry-safe per-call activation state, replacing N3B's
// one-shot Set. NOT_OBSERVED -> PENDING -> CONFIRMED | DEFINITIVE_FAILURE.
// Keyed separately from heartbeatByCallId - a call's heartbeat loop can
// run for its whole lifetime while activation itself only ever resolves
// once (idempotent server-side regardless of how many attempts it takes).
const activationByCallId = new Map();

// N3C section D: one boundary timer per callId, scheduled from the
// server's own serverNowMs/nextChargeAtMs (never Date.now() as the
// authority for WHEN the boundary is).
const boundaryTimerByCallId = new Map();

function logSkipped(callId, reason) {
  console.log(`[heartbeatSync] heartbeat skipped callId=${callId || ""} reason=${reason}`);
}

function logStopped(callId, reason) {
  console.log(`[heartbeatSync] heartbeat stopped callId=${callId || ""} reason=${reason}`);
}

function clearHeartbeatTimer(entry, reason) {
  if (!entry) return;
  if (entry.timerId != null) {
    clearInterval(entry.timerId);
    entry.timerId = null;
  }
  if (reason) {
    logStopped(entry.callId, reason);
  }
}

function clearBoundaryTimer(callId, reason) {
  const key = String(callId || "");
  const timeoutId = boundaryTimerByCallId.get(key);
  if (timeoutId == null) return;
  clearTimeout(timeoutId);
  boundaryTimerByCallId.delete(key);
  if (reason) {
    console.log(`[heartbeatSync] billing_boundary_timer cleared callId=${key} reason=${reason}`);
  }
}

/**
 * Schedules (replacing any previously-scheduled one) a single early tick
 * at nextChargeAtMs, derived only from the server-reported
 * serverNowMs/nextChargeAtMs pair - never from a client-side anchor.
 *
 * N3C1 section B: latencyMs (the measured round-trip of the request that
 * produced `timing`) compensates for the fact the timer is armed only
 * after the response already arrived - serverNowMs is already stale by
 * roughly the round trip. A safe first approximation is that the
 * response transited for about half the measured latency, so the
 * server's clock at the moment this code runs is estimated as
 * serverNowMs + latencyMs/2. Never uses the device wall clock as billing
 * authority - only this one transit estimate on top of server fields.
 */
function scheduleBoundaryTimer(callId, timing, latencyMs, onFire) {
  const key = String(callId || "");
  if (
    !key ||
    !timing ||
    !Number.isFinite(timing.serverNowMs) ||
    !Number.isFinite(timing.nextChargeAtMs)
  ) {
    return;
  }
  clearBoundaryTimer(key);
  const estimatedTransitMs = Math.max(0, Number.isFinite(latencyMs) ? latencyMs / 2 : 0);
  const estimatedServerNowAtReceipt = timing.serverNowMs + estimatedTransitMs;
  const rawDelayMs = timing.nextChargeAtMs - estimatedServerNowAtReceipt;
  // Avoid a zero/negative-delay tight loop when the estimate overshoots.
  const delayMs = rawDelayMs > 0 ? rawDelayMs : BOUNDARY_MIN_RETRY_DELAY_MS;
  console.log(
    `[heartbeatSync] billing_boundary_timer scheduled callId=${key} delayMs=${delayMs}` +
      ` latencyCompensationMs=${estimatedTransitMs} nextChargeAtMs=${timing.nextChargeAtMs}` +
      ` serverNowMs=${timing.serverNowMs}`
  );
  const timeoutId = setTimeout(() => {
    boundaryTimerByCallId.delete(key);
    onFire();
  }, delayMs);
  boundaryTimerByCallId.set(key, timeoutId);
}

/**
 * N3C1 section B: fires the boundary tick unless a heartbeat request is
 * already in flight for this call, in which case it defers with a short
 * bounded retry rather than dropping the tick permanently - the in-flight
 * request's own response will also reschedule the boundary from fresh
 * timing once it completes, so this is a safety net, not the only path.
 */
function fireBoundaryTick(callId) {
  const key = String(callId || "");
  const entry = heartbeatByCallId.get(key);
  if (!entry) return;
  if (entry.inFlight) {
    console.log(`[heartbeatSync] billing_boundary_timer deferred callId=${key} reason=heartbeat_in_flight`);
    const timeoutId = setTimeout(() => fireBoundaryTick(key), BOUNDARY_MIN_RETRY_DELAY_MS);
    boundaryTimerByCallId.set(key, timeoutId);
    return;
  }
  void entry.runTick();
}

function extractTiming(data) {
  if (
    !data ||
    !Number.isFinite(data.serverNowMs) ||
    !Number.isFinite(data.billingStartedAtMs) ||
    !Number.isFinite(data.nextChargeAtMs)
  ) {
    return null;
  }
  return {
    serverNowMs: data.serverNowMs,
    billingStartedAtMs: data.billingStartedAtMs,
    nextChargeAtMs: data.nextChargeAtMs,
  };
}

function getOrCreateActivationEntry(callId) {
  const key = String(callId || "");
  let entry = activationByCallId.get(key);
  if (!entry) {
    entry = { state: "NOT_OBSERVED", generation: 0, attempt: 0, retryTimerId: null, inFlight: false };
    activationByCallId.set(key, entry);
  }
  return entry;
}

function clearActivationState(callId, reason) {
  const key = String(callId || "");
  const entry = activationByCallId.get(key);
  if (!entry) return;
  entry.generation += 1;
  if (entry.retryTimerId != null) {
    clearTimeout(entry.retryTimerId);
    entry.retryTimerId = null;
  }
  entry.inFlight = false;
  const previous = entry.state;
  activationByCallId.delete(key);
  if (previous !== "NOT_OBSERVED") {
    console.log(
      `[heartbeatSync] billing_activation state=cleared callId=${key} reason=${reason} previous=${previous}`
    );
  }
}

/**
 * Stop male Web heartbeat for a call. Safe to call multiple times. Also
 * clears activation state and the boundary timer - this is the single
 * teardown chokepoint every terminal/cleanup path in CallRoomPage.js and
 * NativeCallRoomPage.jsx already calls.
 */
export function stopWebCallHeartbeat(callId, reason = "unknown") {
  const key = String(callId || "");
  if (!key) return;
  clearActivationState(key, reason);
  clearBoundaryTimer(key, reason);
  const entry = heartbeatByCallId.get(key);
  if (!entry) return;
  clearHeartbeatTimer(entry, reason);
  heartbeatByCallId.delete(key);
}

function triggerWebActivationAttempt(callId, source) {
  const entry = activationByCallId.get(callId);
  if (!entry || entry.state !== "PENDING" || entry.inFlight) {
    return;
  }
  console.log(`[heartbeatSync] billing_activation state=invoke callId=${callId} source=${source}`);
  void runWebActivationAttempt(callId, entry.generation);
}

async function runWebActivationAttempt(callId, generation) {
  const entry = activationByCallId.get(callId);
  if (!entry || entry.generation !== generation || entry.state !== "PENDING" || entry.inFlight) {
    return;
  }
  entry.inFlight = true;
  const attemptStartMs = Date.now();
  try {
    const activate = httpsCallable(getFunctions(undefined, "us-central1"), "heartbeatCall");
    const res = await activate({ callId, operation: "media_connected" });
    handleWebActivationResult(callId, generation, res?.data || null, null, Date.now() - attemptStartMs);
  } catch (error) {
    handleWebActivationResult(callId, generation, null, error, Date.now() - attemptStartMs);
  }
}

function handleWebActivationResult(callId, generation, data, error, latencyMs) {
  const entry = activationByCallId.get(callId);
  // callId/generation guard: a stale response for an old call/attempt can
  // never affect a newer one.
  if (!entry || entry.generation !== generation) {
    return;
  }
  entry.inFlight = false;
  if (entry.state !== "PENDING") {
    return;
  }

  if (data && (data.activated || data.alreadyActivated)) {
    entry.state = "CONFIRMED";
    console.log(
      `[heartbeatSync] billing_activation state=${data.activated ? "confirmed" : "already_activated"}` +
        ` callId=${callId} latencyMs=${latencyMs}`
    );
    // N3C section F: a retry that lost the original "activated" response
    // gets alreadyActivated back instead, carrying the same canonical
    // timing fields, so the boundary timer is scheduled correctly either
    // way - the client never needs to guess minute 2's timing.
    const timing = extractTiming(data);
    if (timing) {
      scheduleBoundaryTimer(callId, timing, latencyMs, () => fireBoundaryTick(callId));
    }
    return;
  }

  const code = error?.code || "";
  const permissionDenied = code.endsWith("permission-denied");
  const terminalNonBillable = !!(data && (data.terminal || data.ended));
  if (terminalNonBillable || permissionDenied) {
    entry.state = "DEFINITIVE_FAILURE";
    console.log(
      `[heartbeatSync] billing_activation state=definitive_failure callId=${callId}` +
        ` reason=${permissionDenied ? "permission_denied" : "terminal"} latencyMs=${latencyMs}`
    );
    return;
  }

  // Transient (network/timeout/unauthenticated-needs-refresh/5xx) - bounded
  // backoff retry while the call remains ongoing. Never requires another
  // Zego remote-play event to retry.
  entry.attempt += 1;
  const backoffMs = Math.min(
    ACTIVATION_BASE_BACKOFF_MS * 2 ** Math.min(entry.attempt - 1, 2),
    ACTIVATION_MAX_BACKOFF_MS
  );
  console.log(
    `[heartbeatSync] billing_activation state=retry callId=${callId} attempt=${entry.attempt}` +
      ` backoffMs=${backoffMs} latencyMs=${latencyMs}`
  );
  entry.retryTimerId = setTimeout(() => {
    entry.retryTimerId = null;
    triggerWebActivationAttempt(callId, "scheduled_retry");
  }, backoffMs);
}

/**
 * N3C section E: genuine Web-runtime media-connected activation
 * (CallRoomPage.js only - real Zego web engine, real remote-play-success
 * signal from startPlayingStream resolving and attaching). Never called
 * from route mount or accept status, and never from
 * NativeCallRoomPage.jsx's Web heartbeat fallback (that page hosts a
 * native Android Activity with its own Zego engine and has no
 * independent media signal of its own to trust).
 *
 * Replaces N3B's one-shot Set with the PENDING state above so a transient
 * failure retries automatically (2s/4s/8s/10s-capped) without requiring
 * another remote-play event, and so a regular heartbeat succeeding while
 * PENDING can also nudge/recover the attempt (see maybeRetryWebActivation).
 */
export function sendWebMediaConnectedBilling(callId) {
  const key = String(callId || "");
  if (!key) return;
  const entry = getOrCreateActivationEntry(key);
  if (entry.state !== "NOT_OBSERVED") {
    // Already PENDING/CONFIRMED/DEFINITIVE_FAILURE - a duplicate
    // remote-play event (e.g. a brief re-buffer) is a no-op.
    return;
  }
  entry.state = "PENDING";
  entry.attempt = 0;
  console.log(`[heartbeatSync] billing_activation media_playing_observed callId=${key} source=web_remote_play`);
  triggerWebActivationAttempt(key, "media_playing_observed");
}

/**
 * Called from a successful regular heartbeat tick - if activation is
 * still PENDING (e.g. the dedicated backoff schedule is between
 * attempts), opportunistically nudge it again instead of waiting out the
 * full backoff.
 */
function maybeRetryWebActivationFromHeartbeat(callId) {
  const entry = activationByCallId.get(String(callId || ""));
  if (entry && entry.state === "PENDING") {
    triggerWebActivationAttempt(String(callId || ""), "heartbeat_tick");
  }
}

function shouldRunHeartbeat({ callId, role, status }) {
  if (!callId) return { ok: false, reason: "missing_callId" };
  if (role !== "male") return { ok: false, reason: "role_not_male" };
  if (String(status || "").toLowerCase() !== "ongoing") {
    if (isTerminalCallStatus({ status })) {
      return { ok: false, reason: "terminal" };
    }
    return { ok: false, reason: "status_not_ongoing" };
  }
  return { ok: true, reason: null };
}

/**
 * Keep male Web heartbeat alive for the full ongoing call.
 * Idempotent: does not restart an active timer for the same callId.
 */
export function syncWebCallHeartbeat({
  callId,
  role,
  status,
  onServerEnded,
} = {}) {
  const key = String(callId || "");
  const normalizedStatus = String(status || "").toLowerCase();

  const gate = shouldRunHeartbeat({ callId: key, role, status: normalizedStatus });

  if (!gate.ok) {
    if (key && heartbeatByCallId.has(key)) {
      stopWebCallHeartbeat(key, gate.reason || "gate_closed");
    } else if (key && role === "male") {
      logSkipped(key, gate.reason || "gate_closed");
    }
    return { running: false, reason: gate.reason };
  }

  const existing = heartbeatByCallId.get(key);
  if (existing?.timerId != null) {
    existing.role = role;
    existing.onServerEnded = onServerEnded || existing.onServerEnded;
    return { running: true, reason: "already_running" };
  }

  console.log(
    `[heartbeatSync] heartbeat effect start callId=${key} role=${role} status=${status}`
  );

  const entry = {
    callId: key,
    role,
    tickCount: 0,
    startedAt: Date.now(),
    lastSuccessAt: 0,
    timerId: null,
    onServerEnded: onServerEnded || null,
    inFlight: false,
    runTick: null,
  };

  const heartbeatCall = httpsCallable(getFunctions(undefined, "us-central1"), "heartbeatCall");

  const runTick = async () => {
    if (entry.inFlight) return;
    entry.inFlight = true;
    entry.tickCount += 1;
    const n = entry.tickCount;
    const ageMs = Date.now() - entry.startedAt;
    const tickStartMs = Date.now();
    console.log(`[heartbeatSync] heartbeat tick callId=${key} n=${n} ageMs=${ageMs}`);

    try {
      const res = await heartbeatCall({ callId: key });
      const latencyMs = Date.now() - tickStartMs;
      entry.lastSuccessAt = Date.now();
      console.log(`[heartbeatSync] heartbeat success callId=${key} n=${n} latencyMs=${latencyMs}`);
      if (res?.data?.ended) {
        stopWebCallHeartbeat(key, "server_ended");
        entry.onServerEnded?.(res?.data || {});
        return;
      }
      // N3C/N3C1 section D: reschedule the boundary timer from every
      // committed billing response that carries timing fields
      // (not_due/minute_charged/throttled_with_timing), latency-compensated.
      const timing = extractTiming(res?.data);
      if (timing) {
        scheduleBoundaryTimer(key, timing, latencyMs, () => fireBoundaryTick(key));
      }
      // N3C section E: a successful regular heartbeat can also recover a
      // still-PENDING activation attempt.
      maybeRetryWebActivationFromHeartbeat(key);
    } catch (error) {
      console.warn(`[heartbeatSync] heartbeat warning callId=${key} n=${n}`, error);
    } finally {
      entry.inFlight = false;
    }
  };
  entry.runTick = runTick;

  entry.timerId = setInterval(() => {
    void runTick();
  }, HEARTBEAT_INTERVAL_MS);

  heartbeatByCallId.set(key, entry);
  void runTick();

  return { running: true, reason: "started" };
}

export function isWebCallHeartbeatRunning(callId) {
  const entry = heartbeatByCallId.get(String(callId || ""));
  return !!(entry && entry.timerId != null);
}
